import { useEffect, useState } from 'react';
import { staticImage } from '../models/mapsApi';
import { registerSessionReset, assertSessionCurrent, sessionIsCurrent, sessionTicket } from '../../../utils/sessionLifecycle';

// Chat previews come from our server (/maps/static) as blobs. A small shared
// cache of object URLs means re-renders and reopened chats don't refetch.
const LIMIT = 100;
const cache = new Map(), inflight = new Map();
const keyOf = point => (point ? `${Number(point.latitude).toFixed(6)},${Number(point.longitude).toFixed(6)}` : null);

// Quotes may reuse a map already shown in a bubble, but never initiate a map request.
export function cachedStaticPreview(point) { return cache.get(keyOf(point)) || null; }

function remember(key, url) {
  cache.delete(key); cache.set(key, url);
  while (cache.size > LIMIT) {
    const [oldKey, oldUrl] = cache.entries().next().value;
    cache.delete(oldKey); URL.revokeObjectURL(oldUrl);
  }
}

export function loadStaticPreview(point) {
  const ticket = sessionTicket();
  const key = keyOf(point);
  if (cache.has(key)) { const url = cache.get(key); remember(key, url); return Promise.resolve(url); }
  if (inflight.has(key)) return inflight.get(key);
  const request = staticImage(point)
    .then(blob => { assertSessionCurrent(ticket); const url = URL.createObjectURL(blob); remember(key, url); return url; })
    .finally(() => { if (sessionIsCurrent(ticket)) inflight.delete(key); });
  inflight.set(key, request);
  return request;
}

export function resetStaticPreviewCache() {
  cache.forEach(url => URL.revokeObjectURL(url)); cache.clear(); inflight.clear();
}
registerSessionReset(resetStaticPreviewCache);

// Loads only once the element scrolls into view (or at once without
// IntersectionObserver). Failures leave src null so the card shows no image.
export function useStaticMapPreview(point, elementRef) {
  const key = keyOf(point);
  const [state, setState] = useState({ key: null, src: null, failed: false });
  useEffect(() => {
    if (!key) return undefined;
    let live = true, observer = null;
    const [latitude, longitude] = key.split(',').map(Number);
    const load = () => loadStaticPreview({ latitude, longitude })
      .then(src => { if (live) setState({ key, src, failed: false }); })
      .catch(() => { if (live) setState({ key, src: null, failed: true }); });
    if (cache.has(key) || typeof IntersectionObserver === 'undefined' || !elementRef.current) load();
    else {
      observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect(); observer = null; load();
      }, { rootMargin: '200px' });
      observer.observe(elementRef.current);
    }
    return () => { live = false; observer?.disconnect(); };
  }, [key, elementRef]);
  return state.key === key ? state : { key, src: null, failed: false };
}
